import type { MutableRefObject } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import { v4 } from 'uuid';

import { purchaseCertificateAsync } from '@share/api';
import {
  CertificatePaymentModeEnum,
  type ICertificatePurchaseRequest,
  type ICertificatePurchaseResult,
  type ICertificateTemplate,
} from '@share/common-types';

import type { InstalmentMonthsFormOption, PaymentFormValues } from '../types';

import { MIN_MULTICARD_SPLIT_CARD_AMOUNT_USD } from '../constants';
import type { StripeCardElementsGroup } from '../components/stripe-card-fields';
import {
  billingAddressForSplitFullCard2,
  billingAddressFromFormValues,
} from '../utils/billing-address-from-values';
import { confirmCertificate3DSIfNeeded } from '../utils/certificate-purchase-3ds';
import { parseMoneyInput, roundCurrency2 } from '../utils/payment-math';
import { resolveCertificatePaymentMode } from '../utils/resolve-payment-mode';
import { getCertificateProblemDetailsErrorMessage } from '../utils/certificate-problem-details-error';
import { createPaymentMethodFromGroup } from '../utils/stripe-payment-method';

import type { CertificatePurchaseMessages } from './certificate-purchase-messages';

export type CertificatePurchaseSubmitOutcome =
  | {
      status: 'success';
      result: ICertificatePurchaseResult;
      paymentMode: CertificatePaymentModeEnum;
      instalmentPlanMonths?: InstalmentMonthsFormOption;
    }
  | { status: 'error'; message: string };

interface RunCertificatePurchaseParams {
  stripe: Stripe;
  certificateTemplate: ICertificateTemplate;
  returnUrl: string;
  organizationId: number | undefined;
  certificateTotal: number;
  values: PaymentFormValues;
  card1GroupRef: MutableRefObject<StripeCardElementsGroup | null>;
  card2GroupRef: MutableRefObject<StripeCardElementsGroup | null>;
  messages: CertificatePurchaseMessages;
}

export async function runCertificatePurchase({
  stripe,
  certificateTemplate,
  returnUrl,
  organizationId,
  certificateTotal,
  values,
  card1GroupRef,
  card2GroupRef,
  messages,
}: RunCertificatePurchaseParams): Promise<CertificatePurchaseSubmitOutcome> {
  const mode = resolveCertificatePaymentMode(values);
  const billing = billingAddressFromFormValues(values);

  const baseBody: Pick<
    ICertificatePurchaseRequest,
    | 'templateId'
    | 'returnUrl'
    | 'recipientName'
    | 'recipientEmail'
    | 'name'
    | 'internalNotes'
    | 'validityMonths'
    | 'currency'
    | 'metadata'
    | 'clubSiteId'
  > = {
    templateId: certificateTemplate.id,
    returnUrl,
    recipientName: `${values.firstName} ${values.lastName}`.trim(),
    recipientEmail: values.email,
    name: certificateTemplate.name,
    internalNotes: certificateTemplate.internalNotes ?? '',
    validityMonths: certificateTemplate.validityMonths,
    currency: 'USD',
    metadata: { source: 'web' },
    clubSiteId: organizationId ?? 0,
  };

  const idempotencyKey = v4();
  let body: ICertificatePurchaseRequest;
  let paymentMethodIdFor3ds: string;
  let splitPaymentMethodIds: readonly [string, string] | null = null;

  if (mode === CertificatePaymentModeEnum.PayInFull) {
    const g1 = card1GroupRef.current;
    if (!g1?.cardNumber) {
      return { status: 'error', message: messages.cardDetailsRequired };
    }
    const { error: pmError, paymentMethod } = await createPaymentMethodFromGroup({
      stripe,
      group: g1,
      nameOnCard: values.nameOnCard,
      billing,
    });
    if (pmError || !paymentMethod?.id) {
      return {
        status: 'error',
        message: pmError?.message ?? messages.createPaymentMethodFailed,
      };
    }
    paymentMethodIdFor3ds = paymentMethod.id;
    body = {
      ...baseBody,
      paymentMode: CertificatePaymentModeEnum.PayInFull,
      paymentMethodId: paymentMethod.id,
    };
  } else if (mode === CertificatePaymentModeEnum.PayInFullSplit) {
    const g1 = card1GroupRef.current;
    const g2 = card2GroupRef.current;
    if (!g1?.cardNumber || !g2?.cardNumber) {
      return { status: 'error', message: messages.bothCardsRequired };
    }
    const a1 = parseMoneyInput(values.splitAmount1);
    const a2 = parseMoneyInput(values.splitAmount2);
    if (
      a1 === null ||
      a2 === null ||
      roundCurrency2(a1 + a2) !== roundCurrency2(certificateTotal)
    ) {
      return { status: 'error', message: messages.splitAmountsMismatch };
    }
    if (
      roundCurrency2(a1) < roundCurrency2(MIN_MULTICARD_SPLIT_CARD_AMOUNT_USD) ||
      roundCurrency2(a2) < roundCurrency2(MIN_MULTICARD_SPLIT_CARD_AMOUNT_USD)
    ) {
      return { status: 'error', message: messages.multicardSplitMinAmount };
    }
    const pm1 = await createPaymentMethodFromGroup({
      stripe,
      group: g1,
      nameOnCard: values.nameOnCard,
      billing,
    });
    if (pm1.error || !pm1.paymentMethod?.id) {
      return {
        status: 'error',
        message: pm1.error?.message ?? messages.createPaymentMethodCard1Failed,
      };
    }
    const pm2 = await createPaymentMethodFromGroup({
      stripe,
      group: g2,
      nameOnCard: values.nameOnCard2,
      billing: billingAddressForSplitFullCard2(values),
    });
    if (pm2.error || !pm2.paymentMethod?.id) {
      return {
        status: 'error',
        message: pm2.error?.message ?? messages.createPaymentMethodCard2Failed,
      };
    }
    paymentMethodIdFor3ds = pm1.paymentMethod.id;
    splitPaymentMethodIds = [pm1.paymentMethod.id, pm2.paymentMethod.id];
    body = {
      ...baseBody,
      paymentMode: CertificatePaymentModeEnum.PayInFullSplit,
      paymentMethodId: '',
      splitPayments: [
        { paymentMethodId: pm1.paymentMethod.id, amount: roundCurrency2(a1) },
        { paymentMethodId: pm2.paymentMethod.id, amount: roundCurrency2(a2) },
      ],
    };
  } else if (mode === CertificatePaymentModeEnum.Instalments) {
    const g1 = card1GroupRef.current;
    if (!g1?.cardNumber) {
      return { status: 'error', message: messages.cardDetailsRequired };
    }
    const { error: pmError, paymentMethod } = await createPaymentMethodFromGroup({
      stripe,
      group: g1,
      nameOnCard: values.nameOnCard,
      billing,
    });
    if (pmError || !paymentMethod?.id) {
      return {
        status: 'error',
        message: pmError?.message ?? messages.createPaymentMethodFailed,
      };
    }
    const downParsed = parseMoneyInput(String(values.downPaymentAmount ?? ''));
    if (downParsed === null) {
      return { status: 'error', message: messages.downPaymentRequired };
    }
    const down = roundCurrency2(downParsed);
    paymentMethodIdFor3ds = paymentMethod.id;
    body = {
      ...baseBody,
      paymentMode: CertificatePaymentModeEnum.Instalments,
      paymentMethodId: '',
      primaryPaymentMethodId: paymentMethod.id,
      instalmentMonths: values.instalmentMonths - 1, // -1 because we already have the deposit
      downPaymentAmount: down,
    };
  } else {
    const g1 = card1GroupRef.current;
    const g2 = card2GroupRef.current;
    if (!g1?.cardNumber || !g2?.cardNumber) {
      return { status: 'error', message: messages.bothCardsRequired };
    }
    const downParsedInst = parseMoneyInput(String(values.downPaymentAmount ?? ''));
    if (downParsedInst === null) {
      return { status: 'error', message: messages.downPaymentRequired };
    }
    const down = roundCurrency2(downParsedInst);
    const d1 = parseMoneyInput(values.splitDownAmount1);
    const d2 = parseMoneyInput(values.splitDownAmount2);
    if (d1 === null || d2 === null || roundCurrency2(d1 + d2) !== roundCurrency2(down)) {
      return {
        status: 'error',
        message: messages.downPaymentSplitMismatch,
      };
    }
    if (
      roundCurrency2(d1) < roundCurrency2(MIN_MULTICARD_SPLIT_CARD_AMOUNT_USD) ||
      roundCurrency2(d2) < roundCurrency2(MIN_MULTICARD_SPLIT_CARD_AMOUNT_USD)
    ) {
      return { status: 'error', message: messages.multicardSplitMinAmount };
    }
    const pm1 = await createPaymentMethodFromGroup({
      stripe,
      group: g1,
      nameOnCard: values.nameOnCard,
      billing,
    });
    if (pm1.error || !pm1.paymentMethod?.id) {
      return {
        status: 'error',
        message: pm1.error?.message ?? messages.createPaymentMethodCard1Failed,
      };
    }
    const pm2 = await createPaymentMethodFromGroup({
      stripe,
      group: g2,
      nameOnCard: values.nameOnCard2,
      billing,
    });
    if (pm2.error || !pm2.paymentMethod?.id) {
      return {
        status: 'error',
        message: pm2.error?.message ?? messages.createPaymentMethodCard2Failed,
      };
    }
    const primaryId =
      values.primaryInstalmentCard === 'card1' ? pm1.paymentMethod.id : pm2.paymentMethod.id;
    paymentMethodIdFor3ds = primaryId;
    splitPaymentMethodIds = [pm1.paymentMethod.id, pm2.paymentMethod.id];
    body = {
      ...baseBody,
      paymentMode: CertificatePaymentModeEnum.InstalmentsSplitDownPayment,
      paymentMethodId: '',
      primaryPaymentMethodId: primaryId,
      instalmentMonths: values.instalmentMonths - 1, // -1 because we already have the deposit
      downPaymentAmount: down,
      splitPayments: [
        { paymentMethodId: pm1.paymentMethod.id, amount: roundCurrency2(d1) },
        { paymentMethodId: pm2.paymentMethod.id, amount: roundCurrency2(d2) },
      ],
    };
  }

  const isSplitPaymentMode =
    mode === CertificatePaymentModeEnum.PayInFullSplit ||
    mode === CertificatePaymentModeEnum.InstalmentsSplitDownPayment;

  let result: ICertificatePurchaseResult;

  try {
    const { data: purchaseResult } = await purchaseCertificateAsync(body, idempotencyKey);

    if (isSplitPaymentMode && splitPaymentMethodIds && purchaseResult.paymentLegs?.length) {
      const methodIds = splitPaymentMethodIds;
      const legs = purchaseResult.paymentLegs;
      let resultPromise: Promise<ICertificatePurchaseResult> = Promise.resolve(purchaseResult);
      legs.forEach((leg, legIndex) => {
        resultPromise = resultPromise.then(async (acc) => {
          const legClientSecret = leg.clientSecret ?? acc.clientSecret;
          if (!leg.requires3DS || !legClientSecret) {
            return acc;
          }
          const paymentMethodId = methodIds[legIndex];
          if (!paymentMethodId) {
            throw new Error(
              '3D Secure is required for a payment leg but the card mapping is missing.',
            );
          }
          const complete3dsKey = `${idempotencyKey}:complete-3ds:${leg.id}:${legIndex}`;
          return confirmCertificate3DSIfNeeded({
            stripe,
            result: acc,
            paymentMethodId,
            idempotencyKey: complete3dsKey,
            clientSecretOverride: legClientSecret,
            requires3DSOverride: leg.requires3DS,
            legIndex,
          });
        });
      });
      result = await resultPromise;
      if (result.paymentLegs?.some((leg) => leg.requires3DS)) {
        throw new Error('3D Secure could not be completed for all payment methods.');
      }
    } else {
      result = await confirmCertificate3DSIfNeeded({
        stripe,
        result: purchaseResult,
        paymentMethodId: paymentMethodIdFor3ds,
        idempotencyKey,
      });
    }
  } catch (err: unknown) {
    return {
      status: 'error',
      message: getCertificateProblemDetailsErrorMessage(err, messages.purchaseFailed),
    };
  }

  if (result.purchaseId && (result.httpStatusCode === 202 || result.httpStatusCode === 200)) {
    if (
      mode === CertificatePaymentModeEnum.Instalments ||
      mode === CertificatePaymentModeEnum.InstalmentsSplitDownPayment
    ) {
      return {
        status: 'success',
        result,
        paymentMode: mode,
        instalmentPlanMonths: values.instalmentMonths,
      };
    }
    return { status: 'success', result, paymentMode: mode };
  }
  return {
    status: 'error',
    message: result.providerStatus || result.status || messages.purchaseFailed,
  };
}
